import { Badge, Banner, Button, Checkbox, Field, Input, InputArea, Switch } from '@cloudflare/kumo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppTabs as Tabs } from '../../components/ui/AppTabs';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { scopeHeaders } from '../../lib/scopeHeaders';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { AiToolsPicker } from '../../components/flow-nodes/AiToolsPicker';
import { AgentPublishPanel } from './AgentPublishPanel';
import { AgentTestCasesPanel } from './AgentTestCasesPanel';
import { AgentMetricsPanel } from './AgentMetricsPanel';
import { AgentTurnsPanel } from './AgentTurnsPanel';
import { AgentPersonaFields, type PersonaFields } from './AgentPersonaFields';
import { AgentVoiceFields, type VoiceFields } from './AgentVoiceFields';
import { AgentAvatarFields, type AvatarFields } from './AgentAvatarFields';

interface Agent extends PersonaFields, VoiceFields, Pick<AvatarFields, 'videoAvatar'> { id: string; name: string; persona: string; knowledge: string | null; skillIds: string[]; tools: string[]; model: string | null; active: boolean; publishedVersion?: number | null }
interface SkillLite { id: string; name: string; description: string; active: boolean }
interface Step { tool: string; args: Record<string, unknown>; ok: boolean; result: unknown; ms: number }
interface Turn { role: 'user' | 'assistant'; content: string; steps?: Step[]; skills?: string[]; handoff?: boolean; ms?: number; tokens?: number }
interface Template { id: string; name: string; description: string; tools: string[]; testCases: number }

/** Abas do editor; as quatro últimas precisam do agente salvo */
const TABS = [
  { key: 'persona', label: 'Persona' },
  { key: 'voz', label: 'Voz' },
  { key: 'avatar', label: 'Avatar' },
  { key: 'cerebro', label: 'Cérebro' },
  { key: 'conhecimento', label: 'Conhecimento' },
  { key: 'ferramentas', label: 'Ferramentas' },
  { key: 'handoff', label: 'Handoff' },
  { key: 'testes', label: 'Testes' },
  { key: 'versoes', label: 'Versões' },
  { key: 'metricas', label: 'Métricas' },
  { key: 'observabilidade', label: 'Observabilidade' },
] as const;
type TabKey = typeof TABS[number]['key'];
const NEEDS_SAVED: TabKey[] = ['testes', 'versoes', 'metricas', 'observabilidade'];
type Draft = Omit<Agent, 'id' | 'active'> & { id?: string; active?: boolean };

const emptyPersona = (): PersonaFields => ({ role: '', tone: null, language: 'pt-BR', greeting: null, avatarUrl: null, suggestions: [], pageRules: [] });
const emptyDraft = (): Draft => ({ ...emptyPersona(), name: '', persona: 'Você é a Ana, atendente virtual da empresa. Seja simpática, objetiva e use mensagens curtas.', knowledge: '', skillIds: [], tools: ['transferir_humano'], model: '', voice: null, videoAvatar: null });

/** IA › Agentes: editor em abas (persona, cérebro, conhecimento, ferramentas, handoff, testes, versões, métricas) e playground */
export function AiAgentsPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [skills, setSkills] = useState<SkillLite[]>([]);
  const [locked, setLocked] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Agent | null>(null);
  const [chat, setChat] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [tab, setTab] = useState<TabKey>('persona');
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const end = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/ai/agents');
    const j = await r.json().catch(() => ({}));
    if (r.status === 403) { setLocked(j.message || 'Recurso não incluído no seu plano'); return; }
    if (r.ok) setAgents(j.agents);
    const s = await fetchApi('/api/ai/skills');
    if (s.ok) setSkills((await s.json()).skills);
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [chat, thinking]);

  const save = async (): Promise<string | null> => {
    if (!draft) return null;
    setBusy(true);
    const body = JSON.stringify({
      name: draft.name, persona: draft.persona, knowledge: draft.knowledge, skillIds: draft.skillIds, tools: draft.tools, model: draft.model,
      voice: draft.voice, videoAvatar: draft.videoAvatar, role: draft.role, tone: draft.tone, language: draft.language, greeting: draft.greeting, avatarUrl: draft.avatarUrl,
      suggestions: draft.suggestions.filter((x) => x.trim()),
      pageRules: draft.pageRules.filter((r) => r.path.trim()).map((r) => ({ ...r, suggestions: r.suggestions.filter((x) => x.trim()) })),
    });
    const r = await fetchApi(draft.id ? `/api/ai/agents/${draft.id}` : '/api/ai/agents', { method: draft.id ? 'PUT' : 'POST', body });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível salvar'); return null; }
    toast.success('Agente salvo');
    setDraft({ ...j.agent });
    load();
    return j.agent.id;
  };

  // multipart: o fetch compartilhado força JSON, então este envio monta os cabeçalhos
  const uploadAvatar = async (file: File) => {
    if (!draft?.id) return;
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('auth_token');
    const r = await fetch(`/api/ai/agents/${draft.id}/avatar`, { method: 'POST', headers: { ...scopeHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: form });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível enviar a foto'); return; }
    setDraft((d) => (d ? { ...d, avatarUrl: j.avatarUrl } : d));
    toast.success('Foto enviada');
  };

  const open = (d: Draft) => { setDraft(d); setChat([]); setTab('persona'); };

  const showTemplates = async () => {
    const r = await fetchApi('/api/ai/agent-templates');
    if (r.ok) setTemplates((await r.json()).templates);
  };

  const fromTemplate = async (t: Template) => {
    const r = await fetchApi('/api/ai/agents/from-template', { method: 'POST', body: JSON.stringify({ templateId: t.id }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível criar pelo template'); return; }
    toast.success(`Agente criado com ${t.testCases} caso${t.testCases === 1 ? '' : 's'} de teste. Troque o que está entre [colchetes].`);
    setTemplates(null);
    load();
    open({ ...j.agent, knowledge: j.agent.knowledge ?? '', model: j.agent.model ?? '' });
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !draft) return;
    // O console testa o agente salvo: salva antes de conversar
    const id = draft.id ?? (await save());
    if (!id) return;
    const next: Turn[] = [...chat, { role: 'user', content: text }];
    setChat(next); setInput(''); setThinking(true);
    const t0 = performance.now();
    const r = await fetchApi(`/api/ai/agents/${id}/test`, { method: 'POST', body: JSON.stringify({ messages: next.map(({ role, content }) => ({ role, content })) }) });
    const j = await r.json().catch(() => ({}));
    setThinking(false);
    if (!r.ok) { toast.error(j.message || 'O agente não respondeu'); return; }
    setChat([...next, { role: 'assistant', content: j.reply, steps: j.steps, skills: j.skills, handoff: j.handoff, ms: Math.round(performance.now() - t0), tokens: j.usage ? j.usage.prompt + j.usage.completion : undefined }]);
  };

  if (locked) return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="default" description={<>{locked}</>} /></div>;

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4">
          <div style={{ maxWidth: 760 }}>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Agentes de IA</h2>
            <div className="text-kumo-subtle">
              Um agente é o seu atendente virtual: tem persona, base de conhecimento, <Link to="/ia/skills">skills</Link> (as regras e critérios
              da empresa) e as ferramentas que ele pode usar (gravar dados, entregar cupom, inscrever em sequência…). Use-o no bloco IA das campanhas interativas.
            </div>
          </div>
          {canEdit && !draft && (
            <div className="flex gap-2">
              <Button variant="secondary" icon={ICONS.Copy} onClick={showTemplates} data-testid="ai-agents-templates">Começar por um template</Button>
              <Button variant="primary" icon={ICONS.Plus} onClick={() => open(emptyDraft())} data-testid="ai-agents-new">Novo agente</Button>
            </div>
          )}
        </div>
      </div>

      <div className="page-content space-y-6">
        {!draft && templates && (
          <section className="brand-card space-y-3" data-testid="ai-agent-templates">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Templates</h3>
              <Button variant="secondary" onClick={() => setTemplates(null)}>Fechar</Button>
            </div>
            <div className="text-xs !text-[var(--text-muted)]">Vem com persona, ferramentas e casos de teste. Depois é só trocar o que está entre [colchetes] e completar a base de conhecimento.</div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
              {templates.map((t) => (
                <div key={t.id} className="p-3 space-y-2" style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-sm !text-[var(--text-muted)]">{t.description}</div>
                  <div className="text-xs !text-[var(--text-muted)]">{t.tools.length} ferramentas · {t.testCases} caso{t.testCases === 1 ? '' : 's'} de teste</div>
                  <Button variant="primary" onClick={() => fromTemplate(t)} data-testid={`ai-agent-template-${t.id}`}>Usar este</Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {!draft && agents && agents.length === 0 && <Banner variant="default" description={<>Nenhum agente ainda. Crie um, escolha as skills e teste a conversa aqui mesmo antes de usar no fluxo.</>} />}

        {!draft && agents && agents.length > 0 && (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {agents.map((a) => (
              <section key={a.id} className="brand-card space-y-2" data-testid={`ai-agent-card-${a.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{a.name}</div>
                  <div className="flex gap-1">
                    {a.publishedVersion ? <Badge variant="blue">v{a.publishedVersion} no ar</Badge> : null}
                    <Badge variant={a.active ? 'green' : 'neutral'}>{a.active ? 'Ativo' : 'Pausado'}</Badge>
                  </div>
                </div>
                <div className="text-sm !text-[var(--text-muted)]">{a.skillIds.length} skill{a.skillIds.length === 1 ? '' : 's'} · {a.tools.length} ferramenta{a.tools.length === 1 ? '' : 's'} fixa{a.tools.length === 1 ? '' : 's'}</div>
                <div className="flex gap-2 pt-1">
                  <Button variant="secondary" icon={canEdit ? ICONS.Edit : ICONS.Visibility} data-testid={`ai-agent-edit-${a.id}`} onClick={() => open({ ...a, knowledge: a.knowledge ?? '', model: a.model ?? '' })}>{canEdit ? 'Editar e testar' : 'Ver'}</Button>
                  {canEdit && <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => setDeleting(a)}>Excluir</DangerButton>}
                </div>
              </section>
            ))}
          </div>
        )}

        {draft && (
          <Tabs
            activeTabId={tab}
            onChange={({ detail }) => setTab(detail.activeTabId as TabKey)}
            tabs={TABS.map((t) => ({ id: t.key, label: t.label }))}
            disableContentPaddings
            ariaLabel="Seções do agente"
          />
        )}
        {draft && (
          <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 1fr)' }}>
            <section className="brand-card space-y-4">
              {tab === 'persona' && (
                <>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Field label="Nome">
                        <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} placeholder="Ana (vendas)" />
                      </Field>
                    </div>
                    {draft.id && canEdit && (
                      <label className="flex items-center gap-2 pb-2"><Switch onCheckedChange={async (checked: boolean) => { const v = checked; const r = await fetchApi(`/api/ai/agents/${draft.id}`, { method: 'PUT', body: JSON.stringify({ active: v }) }); if (r.ok) { setDraft({ ...draft, active: v }); load(); } }} aria-label="Ativo" checked={draft.active !== false} /><div>Ativo</div></label>
                    )}
                  </div>
                  <InputArea label="Persona e regras" description="Quem é, tom de voz e o que nunca fazer. O agente sempre se apresenta como assistente virtual (IA)." onChange={(e) => setDraft({ ...draft, persona: e.target.value })} rows={10} value={draft.persona} />
                  <AgentPersonaFields value={draft} name={draft.name} readOnly={!canEdit} onChange={(patch) => setDraft({ ...draft, ...patch })} onUpload={draft.id ? uploadAvatar : undefined} />
                </>
              )}

              {tab === 'avatar' && <AgentAvatarFields value={draft} readOnly={!canEdit} onChange={(patch) => setDraft({ ...draft, ...patch })} />}
              {tab === 'voz' && <AgentVoiceFields value={draft} readOnly={!canEdit} onChange={(patch) => setDraft({ ...draft, ...patch })} />}

              {tab === 'cerebro' && (
                <>
                  <Field label="Modelo (opcional)" description="Vazio usa o modelo padrão da plataforma, com o modelo de reserva configurado em Plataforma › Integrações">
                    <Input onChange={(e) => setDraft({ ...draft, model: e.target.value })} value={draft.model || ''} />
                  </Field>
                  <div className="text-xs !text-[var(--text-muted)]">A versão publicada responde pelo AI Gateway da Cloudflare: se o modelo principal falhar, a reserva assume sem o cliente perceber.</div>
                </>
              )}

              {tab === 'conhecimento' && (
                <>
                  <InputArea label="Base de conhecimento" description={`${(draft.knowledge || '').length.toLocaleString('pt-BR')} / 12.000 caracteres: preços, horários, políticas…`} onChange={(e) => setDraft({ ...draft, knowledge: e.target.value })} rows={10} value={draft.knowledge || ''} />
                  <div className="text-xs !text-[var(--text-muted)]">Documentos e páginas do site entram em <Link to="/ia/base">IA › Base de conhecimento</Link> e ficam disponíveis para a versão publicada.</div>
                  <div>
                    <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Skills</h3>
                    <div className="text-xs !text-[var(--text-muted)]">Conhecimento procedural que o agente consulta quando precisa.</div>
                    {skills.length === 0
                      ? <div className="text-sm !text-[var(--text-muted)]">Nenhuma skill criada. <Link to="/ia/skills">Crie ou adicione da galeria</Link>.</div>
                      : <div className="space-y-2 mt-2">{skills.map((sk) => (
                        <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, skillIds: checked ? [...draft.skillIds, sk.id] : draft.skillIds.filter((x) => x !== sk.id) })} key={sk.id} checked={draft.skillIds.includes(sk.id)} labelTooltip={sk.description} label={<>
                          {`${sk.name}${sk.active ? '' : ' (pausada)'}`}
                        </>} />
                      ))}</div>}
                  </div>
                </>
              )}

              {tab === 'ferramentas' && <AiToolsPicker value={draft.tools} onChange={(tools) => setDraft({ ...draft, tools })} />}

              {tab === 'handoff' && (
                <div className="space-y-3">
                  <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, tools: checked ? [...new Set([...draft.tools, 'transferir_humano'])] : draft.tools.filter((x) => x !== 'transferir_humano') })} checked={draft.tools.includes('transferir_humano')} labelTooltip="Quando o cliente pede uma pessoa, reclama ou o assunto foge da base, a IA para de responder e a conversa vai para a Caixa de entrada." label={<>
                    Pode passar a conversa para a equipe
                  </>} />
                  <div className="text-sm">Quando passar, escreva na persona as regras: por exemplo, “transfira quando o cliente pedir cancelamento ou falar de cobrança”.</div>
                  <div className="text-xs !text-[var(--text-muted)]">No widget do site o visitante vê o aviso “Nossa equipe vai continuar o atendimento” e, se configurado em <Link to="/ia/widget">IA › Widget</Link>, o botão para continuar no WhatsApp.</div>
                </div>
              )}

              {NEEDS_SAVED.includes(tab) && !draft.id && <Banner variant="default" description={<>Salve o agente para criar casos de teste, publicar e ver métricas.</>} />}
              {tab === 'testes' && draft.id && <AgentTestCasesPanel agentId={draft.id} canEdit={canEdit} />}
              {tab === 'versoes' && draft.id && <AgentPublishPanel agentId={draft.id} canEdit={canEdit} onChange={load} />}
              {tab === 'metricas' && draft.id && <AgentMetricsPanel agentId={draft.id} />}
              {tab === 'observabilidade' && draft.id && <AgentTurnsPanel agentId={draft.id} />}

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => { setDraft(null); setChat([]); }} data-testid="ai-agent-close">Fechar</Button>
                {canEdit && <Button variant="primary" loading={busy} disabled={!draft.name.trim()} onClick={save} data-testid="ai-agent-save">Salvar</Button>}
              </div>
            </section>

            <div className="space-y-6">
            <section className="brand-card flex flex-col" style={{ minHeight: 520 }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Playground</h3>
                <Badge variant="blue">Simulação: nada é gravado</Badge>
              </div>
              <div className="text-xs !text-[var(--text-muted)]">Fale como um cliente com a configuração salva. O trace mostra skills ativadas, ferramentas (argumentos e resultado), tempo e tokens de cada resposta.</div>
              <div className="flex-1 overflow-y-auto space-y-3 my-3 rounded-md p-3" style={{ maxHeight: 520, backgroundImage: 'radial-gradient(var(--border) 1px, transparent 1px)', backgroundSize: '16px 16px' }}>
                {chat.length === 0 && <div className="text-center text-sm !text-[var(--text-muted)] py-10">Experimente uma pergunta para começar</div>}
                {chat.map((t, i) => (
                  <div key={i} className={t.role === 'user' ? 'text-right' : ''}>
                    <div className={`flex items-start gap-2 ${t.role === 'user' ? 'justify-end' : ''}`}>
                      {t.role !== 'user' && <span className="jev-avatar jev-avatar--jev" aria-hidden><ICONS.Sparkle size={16} /></span>}
                      <div data-testid={`ai-agent-test-${t.role}`} style={{ display: 'inline-block', maxWidth: '85%', padding: '8px 12px', borderRadius: 12, textAlign: 'left', whiteSpace: 'pre-wrap',
                        background: t.role === 'user' ? 'var(--crm-accent, #0051c3)' : 'var(--surface)', color: t.role === 'user' ? 'var(--crm-on-accent, #fff)' : undefined,
                        border: t.role === 'user' ? 'none' : '1px solid var(--border)', boxShadow: t.role === 'user' ? undefined : '0 1px 4px rgba(0,0,0,.06)' }}>{t.content}</div>
                      {t.role === 'user' && <span className="jev-avatar jev-avatar--user" aria-hidden><ICONS.Person size={16} /></span>}
                    </div>
                    {(t.skills?.length || t.steps?.length || t.handoff || t.ms !== undefined) ? (
                      <div className="mt-1 space-y-1">
                        {t.skills?.map((s) => <Badge variant="green" key={s} className="mr-1">skill: {s}</Badge>)}
                        {t.handoff && <Badge variant="orange">transferiu para a equipe</Badge>}
                        {t.ms !== undefined && <div className="text-xs !text-[var(--text-muted)]">{(t.ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s{t.tokens ? ` · ${t.tokens.toLocaleString('pt-BR')} tokens` : ''}{t.steps?.length ? ` · ${t.steps.length} ferramenta${t.steps.length === 1 ? '' : 's'}` : ''}</div>}
                        {t.steps?.map((s, j) => (
                          <details key={j} className="text-xs" style={{ color: s.ok ? 'var(--text-2)' : 'var(--crm-text-error)' }}>
                            <summary style={{ cursor: 'pointer' }}>{s.ok ? '✓' : '✗'} {s.tool} ({s.ms} ms)</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2)', padding: 6, borderRadius: 6 }}>{JSON.stringify({ argumentos: s.args, resultado: s.result }, null, 2)}</pre>
                          </details>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {thinking && <div className="text-sm !text-[var(--text-muted)]">digitando…</div>}
                <div ref={end} />
              </div>
              <form className="flex gap-2 items-center" data-testid="ai-agent-test-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <div className="flex-1">
                  <Input className="w-full" onChange={(e) => setInput(e.target.value)} aria-label="Mensagem do cliente" placeholder="Oi, quanto custa?" value={input} disabled={!canEdit} />
                </div>
                <Button type="submit" variant="primary" icon={ICONS.ArrowRight} disabled={!input.trim() || thinking || !canEdit || !draft.name.trim()} data-testid="ai-agent-test-send">Enviar</Button>
              </form>
              {chat.length > 0 && <button type="button" className="ask-clear mt-2" onClick={() => setChat([])}><ICONS.Trash size={14} /> Limpar conversa</button>}
            </section>
            </div>
          </div>
        )}
      </div>

      <DangerConfirmModal open={!!deleting} title="Excluir agente" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) { const r = await fetchApi(`/api/ai/agents/${deleting.id}`, { method: 'DELETE' }); if (r.ok) load(); } setDeleting(null); }}>
        Blocos IA que usam <strong>{deleting?.name}</strong> voltam a responder só com as próprias instruções. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}
